import { Reducer } from 'react';

/**
 * State for the sample table grid.
 * Collapse state is intentionally excluded — it is owned by useStudiesStore
 * (persisted per-study via SampleTableAccordion).
 */
export interface SampleTableGridState {
  pageSize: number;
  totalCount: number | null;
  /** Row count on the current page — may be less than pageSize on the last page. */
  currentPageRowCount: number | null;
  firstDataRendered: boolean;
}

/**
 * Actions for the sample table grid reducer.
 */
export type SampleTableGridAction =
  | { type: 'SET_PAGE_SIZE'; value: number }
  | { type: 'SET_TOTAL_COUNT'; value: number | null }
  | { type: 'SET_CURRENT_PAGE_ROW_COUNT'; value: number | null }
  | { type: 'SET_FIRST_DATA_RENDERED'; value: boolean };

/**
 * Returns the initial state for the sample table grid.
 * Reads persisted page size from localStorage; all other state is derived at runtime.
 */
export function getInitialState(
  studyId: string,
  paginationPageSizeKeyFn: (studyId: string) => string,
  defaultPageSize: number,
): SampleTableGridState {
  const saved = localStorage.getItem(paginationPageSizeKeyFn(studyId));
  const parsedPageSize = saved ? Number.parseInt(saved, 10) : defaultPageSize;
  const pageSize = Number.isNaN(parsedPageSize) ? defaultPageSize : parsedPageSize;

  return {
    pageSize,
    totalCount: null,
    currentPageRowCount: null,
    firstDataRendered: false,
  };
}

/**
 * Reducer for the sample table grid state.
 */
export const sampleTableGridReducer: Reducer<SampleTableGridState, SampleTableGridAction> = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.value };
    case 'SET_TOTAL_COUNT':
      return { ...state, totalCount: action.value };
    case 'SET_CURRENT_PAGE_ROW_COUNT':
      return { ...state, currentPageRowCount: action.value };
    case 'SET_FIRST_DATA_RENDERED':
      return { ...state, firstDataRendered: action.value };
    default:
      return state;
  }
};
